import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as PropTypes from 'prop-types';
import {
  Icon, Affix, Row, Typography, Menu, Tooltip,
} from 'antd';
import ViaDropMenu from '../menu/ViaDropMenu';
import configMenuStructure from '../../sections/secured/configure/config-menu';
import userMenuStructure from '../../sections/secured/dashboard/user-menu';
import routeLocations, { documentationRoutes } from '../../utility/route-locations';
import inspectMenuStructure from '../../sections/secured/dashboard/inspections-menu';
import creditsMenuStructure from '../../sections/secured/dashboard/credits-menu';
import { requestSignOut } from '../../state/ui/application/sessionUiActions';
import { selectSessionUser } from '../../state/ui/application/sessionUiReducer';
import ViaSearch from '../search/ViaSearch';
import useIsMobileSize from '../../utility/hooks/useIsMobileSize';
import useElementSize from '../../utility/hooks/useElementSize';
import { ViaLogo, LogoLocations } from '../logo/ViaLogo';

const { Text } = Typography;
const { SubMenu } = Menu;


/**
 * The mobile version of the menu.
 * @param {object} props The react props.
 * @param {string} props.username The sign in user.
 * @param {number} props.creditBalance The credit balance of the user group.
 * @param {func} props.onSignOut A function that is executed when the user attempts to signout.
 * @param {boolean} props.showCredits Show the credits menu or not
 */
function MobileMenu({ username, creditBalance, onSignOut, showCredits }) {
  const menuClick = useCallback((e) => {
    if (e.key === 'sign-out') {
      onSignOut();
    }
  }, [onSignOut]);

  return (
      <Menu mode="horizontal" forceSubMenuRender>
        <SubMenu
          title={(
            <Row className="via-header-menu-button" style={{ marginLeft: '1em', marginRight: '1em' }}>
              <Icon type="menu" />
              <FormattedMessage id="menu" />
            </Row>
        )}
        >
          <Menu.Item key="x0">
            <ViaDropMenu
              mode="vertical"
              menuStructure={null}
              subMenuTitle={(
                <Link to={documentationRoutes.user_guide} target="_blank">
                  <Row className="via-mobile-drop-submenu link">
                    <Icon type="info-circle" />
                    <FormattedMessage id="userGuide" />
                  </Row>
                </Link>
              )}
            />
          </Menu.Item>
          <Menu.Item key="x1">
            <ViaDropMenu
              mode="vertical"
              menuStructure={inspectMenuStructure}
              subMenuTitle={(
                <Link to={routeLocations.inspectionList}>
                  <Row className="via-mobile-drop-submenu link">
                    <Icon type="file-done" />
                    <FormattedMessage id="inspections" />
                  </Row>
                </Link>
            )}
            />
          </Menu.Item>
          <Menu.Item key="x2">
            <ViaDropMenu
              mode="horizontal"
              menuStructure={configMenuStructure}
              subMenuTitle={(
                <Link className="via-mobile-drop-submenu" to={routeLocations.configure}>
                  <Row>
                    <Icon type="setting" />
                    <FormattedMessage id="configure" />
                  </Row>
                </Link>
            )}
            />
          </Menu.Item>
          <Menu.Item key="x4">
            <ViaDropMenu
              mode="horizontal"
              menuStructure={userMenuStructure}
              click={menuClick}
              subMenuTitle={(
                <Row className="via-mobile-drop-submenu">
                  <Icon type="user" />
                  {username}
                </Row>
            )}
            />
          </Menu.Item>
          { showCredits ? (
          <Menu.Item key="x5">
            <ViaDropMenu
              mode="horizontal"
              menuStructure={creditsMenuStructure}
              subMenuTitle={(
                <Row className="via-mobile-drop-submenu">
                  <Text className="via-mobile-drop-submenu-text">{creditBalance}</Text>
                  <Icon className="via-mobile-drop-submenu-text" type="tags" theme="filled" />
                </Row>
            )}
            />
          </Menu.Item>
          ) : (<></>) }
        </SubMenu>
      </Menu>
  );
}

MobileMenu.propTypes = {
  username: PropTypes.string.isRequired,
  creditBalance: PropTypes.number,
  onSignOut: PropTypes.func.isRequired,
  showCredits: PropTypes.bool,
};
MobileMenu.defaultProps = {
  creditBalance: null,
};


/**
 * The standard version of the menu.
 * @param {object} props The react props.
 * @param {string} props.username The sign in user.
 * @param {number} props.creditBalance The credit balance of the user group.
 * @param {func} props.onSignOut A function that is executed when the user attempts to signout.
 * @param {boolean} props.showCredits Show the credits menu or not
 */
function StandardMenu({ username, creditBalance, onSignOut, showCredits }) {
  const menuClick = useCallback((e) => {
    if (e.key === 'sign-out') onSignOut();
  }, [onSignOut]);

  return (
    <>
      <ViaDropMenu
        mode="horizontal"
        menuStructure={inspectMenuStructure}
        subMenuTitle={(
          <Link to={routeLocations.inspectionList}>
            <Row className="via-header-menu-button link" type="flex" align="middle">
              <Icon type="file-done" />
              <FormattedMessage id="inspections" />
            </Row>
          </Link>
        )}
      />
      <ViaDropMenu
        mode="horizontal"
        menuStructure={configMenuStructure}
        subMenuTitle={(
          <Row className="via-header-menu-button" type="flex" align="middle">
            <Icon type="setting" />
            <FormattedMessage id="configure" />
          </Row>
        )}
      />
      {/* <ViaDropMenu
        mode="horizontal"
        menuStructure={helpMenuStructure}
        subMenuTitle={(
          <Row className="via-header-menu-button" type="flex" align="middle">
            <Icon type="question" />
            <FormattedMessage id="helpHeader" />
          </Row>
        )}
      /> */}
      <ViaDropMenu
        mode="horizontal"
        menuStructure={userMenuStructure}
        click={menuClick}
        subMenuTitle={(
          <Row className="via-header-menu-button" type="flex" align="middle">
            <Icon type="user" />
            {username}
          </Row>
        )}
      />
      { showCredits ? (
      <ViaDropMenu
        mode="horizontal"
        menuStructure={creditsMenuStructure}
        subMenuTitle={(
          <Row className="via-header-menu-button">
            <Text className="via-header-credits-text">{creditBalance}</Text>
            <Icon className="via-header-credits-icon" type="tags" theme="filled" style={{ marginRight: '0', paddingRight: '0' }} />
          </Row>
        )}
      />
      ) : (<></>) }
      <Row className="via-header-credits-icon" style={{ fontSize: '18pt', marginTop: '0.1em', cursor: 'pointer' }}>
        <Tooltip placement="bottomLeft" title={<FormattedMessage id="userGuide" />}>
          <Link to={documentationRoutes.user_guide} target="_blank"><Icon type="info-circle" /></Link>
        </Tooltip>
      </Row>
    </>
  );
}

StandardMenu.propTypes = {
  username: PropTypes.string.isRequired,
  creditBalance: PropTypes.number,
  onSignOut: PropTypes.func.isRequired,
  showCredits: PropTypes.bool,
};
StandardMenu.defaultProps = {
  creditBalance: null,
};


const useViaSearchRef = () => {
  const [searchElem, setSearchElem] = useState();
  const searchRef = useCallback((element) => {
    if (element) setSearchElem(element);
  }, [setSearchElem]);

  return [searchRef, searchElem];
};


/**
 * The application header for authenticated users.
 * @param {object} props The react props.
 * @param {boolean} props.showCredits Show credits menu or not.
 */
export default function ViaHeader({
    showCredits,
}) {
  // Redux Hooks
  const sessionUser = useSelector(selectSessionUser);
  const username = sessionUser ? sessionUser.displayName : '';
  const creditBalance = sessionUser && sessionUser.userGroup && sessionUser.userGroup.tokensAvailable ? sessionUser.userGroup.tokensAvailable : 0;
  const dispatch = useDispatch();
  const onSignOut = useCallback(() => { dispatch(requestSignOut()); }, [dispatch]);

  // React Hooks
  const [searchRef, searchElem] = useViaSearchRef();
  const [searchWidth] = useElementSize(searchElem);
  const isMobile = useIsMobileSize(); // TODO: Instead of using a hook, we can just use a simple css media query to hide/show the menu items.


  return (// Ant messages and notifications have z-index of 1010 so dont make this any higher than that!
    <header style={{ zIndex: '999' }}>
      <Affix offsetTop={0} style={{ height: '50px' }}>
        <div className="via-header-layout">
          <div className="via-header-view">
            <ViaLogo href={routeLocations.dashboard} logoSrc={LogoLocations.NORMAL_BLUE_CIRCLE_WHITE_M} className="via-header-logo" />
            <div ref={searchRef} className="via-header-search">
              { searchWidth > 200 && <ViaSearch style={{ width: '100%' }} /> }
            </div>
            <div className="via-header-links">
              {
              isMobile
                ? <MobileMenu creditBalance={creditBalance} username={username} onSignOut={onSignOut} showCredits={showCredits} />
                : <StandardMenu creditBalance={creditBalance} username={username} onSignOut={onSignOut} showCredits={showCredits} />
              }
            </div>
          </div>
        </div>
      </Affix>
    </header>
  );
}

// Validate prop type parameters.
ViaHeader.propTypes = {
    showCredits: PropTypes.bool,
};

ViaHeader.defaultProps = {
    showCredits: true,
};